<script setup lang="ts">
import { computed } from "vue";
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { AtomButton, AtomText } from "@chatfood/design-system";
import { LoyaltyNativeBalance } from "~/components/loyalty-native-balance";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";
import { useModalHubStore } from "~/components/modal-hub";
import { useLocale } from "~/composables/use-locale";
import { useFeatureMatrixStore } from "#imports";

const css = bemBuilder("loyalty-landing-page");

const { t } = useLocale();

const businessStore = useBusinessStore();
const customerStore = useCustomerStore();
const loyaltyProgramStore = useLoyaltyProgramStore();
const featureMatrixStore = useFeatureMatrixStore();

const modalHub = useModalHubStore();

const signupBonus = computed(() =>
  toMoney(loyaltyProgramStore.signupPoints, businessStore.currency),
);

const cashbackPercentage = computed(
  () => loyaltyProgramStore.orderAmountPercentageWorth,
);

const maxCashbackPercentage = computed(
  () => loyaltyProgramStore.maximumRedeemedOrderPercentage,
);

const handleSignUp = () => {
  modalHub.showAuthManager();
};
</script>

<template>
  <div :class="css()">
    <div
      v-if="customerStore.isLogged"
      :class="css('logged')"
      data-test="customer-logged-content"
    >
      <div>
        <AtomText type="h3" :class="css('logged-greetings')">
          {{
            t("components.loyalty_landing_page.greetings", {
              customerName: customerStore.name,
            })
          }}
        </AtomText>
        <AtomText color="color-base-60">
          {{
            t("components.loyalty_landing_page.logged_description", {
              maxCashbackPercentage,
              cashbackPercentage,
            })
          }}
        </AtomText>
      </div>
      <div>
        <LoyaltyNativeBalance />
      </div>
    </div>
    <div
      v-else
      :class="[
        css('not-logged'),
        { [css('not-logged', 'center')]: featureMatrixStore.skipAuthFlow },
      ]"
      data-test="customer-not-logged-content"
    >
      <div>
        <AtomText type="h3">
          {{ t("components.loyalty_landing_page.not_logged_line_1") }}
        </AtomText>
        <AtomText
          v-if="loyaltyProgramStore.signupPoints > 0"
          type="h3"
          color="color-primary"
          data-test="signup-bonus-text"
        >
          {{
            t("components.loyalty_landing_page.not_logged_line_2", {
              signupBonus,
            })
          }}
        </AtomText>
        <AtomText color="color-base-60">
          {{
            t("components.loyalty_landing_page.not_logged_description", {
              cashbackPercentage,
            })
          }}
        </AtomText>
      </div>
      <div
        v-if="!featureMatrixStore.skipAuthFlow"
        :class="css('not-logged-cta')"
      >
        <AtomButton
          wide
          size="large"
          type="button"
          theme="ghost"
          variant="secondary"
          data-test="signup-cta"
          @click.prevent="handleSignUp"
        >
          {{ t("components.loyalty_landing_page.signup_cta") }}
        </AtomButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.loyalty-landing-page {
  padding: var(--spacing-md);
  border: solid 1px var(--color-base-90);
  background-color: var(--color-base-100);

  @media (width > $breakpoint-sm) {
    padding: var(--spacing-lg);
    border-radius: var(--radii-card-md);
  }

  &__logged {
    display: flex;
    gap: var(--spacing-md);

    &-greetings {
      margin-bottom: var(--spacing-sm);
    }
  }

  &__not-logged {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);

    @media (width > $breakpoint-sm) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      &-cta {
        min-width: 160px;
      }
    }

    &--center {
      align-items: center;
      justify-content: center;
    }
  }
}
</style>
